import type {SlotGameStore, BaseComponentConfig, ReelsetComponent} from "@gamzix/slot_core";

class BigWinComponent extends Game.src.component.BaseComponent {
    protected waitClick: () => Promise<void>;
    protected isWinAllLabel: boolean = false;
    protected timeWaitAfterEnd: number = 2000;
    protected particleContainer;
    protected backgroundParticleContainer;
    protected container;
    protected bigWin;
    protected text;

    protected multiplier = 10;

    static events = {
        START: "BigWinComponent.START",
        END: "BigWinComponent.END",
        START_COUNT: "BigWinComponent.START_COUNT",
        END_COUNT: "BigWinComponent.END_COUNT"
    }

    constructor(config: BaseComponentConfig) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isWinAllLabel: () => this.isWinAllLabel
        };
    }

    onGameReady() {
        game.once('SET_WAIT_CLICK', ({waitClick}) => this.waitClick = waitClick);

        super.onGameReady();

        this.bigWin = game.tree.findByName("bigwin.bigwin_texture");
        this.container = game.tree.findByName("bigwin.bigwin_container");
        this.text = game.tree.findByName("bigwin.bigwin_label");
        this.backgroundParticleContainer = game.tree.findByName('bigwin.bigwin_coin_container');
    }

    checkBigWin(){
        if ((<SlotGameStore>game.store).gameData.spinWinAmount >= (<SlotGameStore>game.store).gameData.bet * this.multiplier){
            return true;
        }
    }

    get reelset(): ReelsetComponent {
        return <any>game.component.reelset;
    }

    async showBigWinTexturePopup(){
        game.utils.GameUtils.playAlphaAnimation(this.container, {from: 0, to: 1, time: 300, timeWait: 0});

        await game.utils.GameUtils.playScaleToAnimation(this.bigWin, {scaleFrom: 0, scaleTo: 1.12, time: 750});
        await game.utils.GameUtils.playScaleToAnimation(this.bigWin, {scaleFrom: 1.12, scaleTo: 1, time: 1200});
    }

    async showStartBigwinPopup() {
        this.setParentGroupTopForGameMachineFrame()

        this.createCoinParticles();
        this.showBigWinTexturePopup();

        game.emit(BigWinComponent.events.START);
        game.emit(BigWinComponent.events.START_COUNT);

        await Promise.race([
            this.waitClick(),
            game.onceDataPromise('Button.clicked', { component: 'bigWin', name: 'bigWin.click_button'}),
            game.utils.GameUtils.playCounterAnimation(this.text, { from: 0, to: (<SlotGameStore>game.store).gameData.spinWinAmount, time: 3000, timeWait: 0 })
        ]);

        game.utils.removeCall(this.text._tween);

        this.text.options.text = (<SlotGameStore>game.store).toFormatMoney((<SlotGameStore>game.store).gameData.spinWinAmount);
        this.text.updateViewOptions();

        game.emit(BigWinComponent.events.END_COUNT);
        game.emit(BigWinComponent.events.END);

        await game.utils.delay(this.timeWaitAfterEnd);
    }

    async hideBigwinPopup() {
        await game.utils.GameUtils.playAlphaAnimation(this.container, {from: 1, to: 0, time: 300, timeWait: 0});

        game.utils.PIXIUtils.remove(this.particleContainer);

        this.text.options.text = "";

        this.setParentGroupGameForGameMachineFrame();
    }

    async createCoinParticles() {
        await game.utils.delay(350);

        const particlesSmallUnder = this.createParticlesItem(
            'coinParticle',
            ["coin_00","coin_01","coin_02","coin_03","coin_04","coin_05","coin_06","coin_07","coin_08","coin_09","coin_10"]);

        this.backgroundParticleContainer.addChild(particlesSmallUnder);
    }

    createParticlesItem(nameParticle: string, nameTextures: any){
        const config = {
            "alpha": {
                "start": 1,
                "end": 1
            },
            "scale": {
                "start": 1,
                "end": 1,
                "minimumScaleMultiplier": 1
            },
            "color": {
                "start": "#ffffff",
                "end": "#ffffff"
            },
            "speed": {
                "start": 3000,
                "end": 0,
                "minimumSpeedMultiplier": 0.6
            },
            "acceleration": {
                "x": 0,
                "y": 3500
            },
            "maxSpeed": 0,
            "startRotation": {
                "min": 250,
                "max": 290
            },
            "noRotation": false,
            "rotationSpeed": {
                "min": 100,
                "max": 360
            },
            "lifetime": {
                "min": 4,
                "max": 8
            },
            "blendMode": "normal",
            "frequency": 0.04,
            "emitterLifetime": -1,
            "maxParticles": 1500,
            "pos": {
                "x": 0,
                "y": 630
            },
            "addAtBack": false,
            "spawnType": "point"
        }

        return this.particleContainer = game.builder.create({
            "type": "Particles",
            "name": nameParticle,
            "framerate": 30,
            "loop": true,
            'textures': nameTextures,
            "isStart": true,
            "particlesConfig": config,
        });
    }

    setParentGroupTopForGameMachineFrame() {
        const gameMachineFrame = game.tree.findByName('reel_frame.frame');

        this.setParentGroup(gameMachineFrame, "TOP");
        this.setZOrder(gameMachineFrame, 2);

        this.onChangeOrientation();
    }

    setParentGroupGameForGameMachineFrame() {
        const gameMachineFrame = game.tree.findByName('reel_frame.frame');

        this.setParentGroup(gameMachineFrame, "GAME");
        this.setZOrder(gameMachineFrame, 3);

        this.onChangeOrientation();
    }

    setZOrder(obj, zOrder) {
        obj.zOrder = zOrder;
    }

    setParentGroup(obj, parentGroupName) {
        obj.parentGroup = game.render.layerGroups[parentGroupName];
    }

    getFSMHandlers() {
        return {
            SHOW_WIN: {
                showBigWin: {
                    on: async () => {
                        this.reelset.playAllWinsLoopAnimation();

                        this.isWinAllLabel = true;

                        await this.showStartBigwinPopup();
                        await this.hideBigwinPopup();

                        this.reelset.playAllIdleAnimation();
                    },
                    check: ()=> this.checkBigWin()
                }
            }
        }
    }

}

//@ts-ignore
Game.src.component.BigWinComponent = BigWinComponent;
